import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { ShipmentManagementPickupDeliveryDetails } from "~/shared/schemas/shipment-management-schema.ts";
import type { StopName } from "~/shared/types/stop-types.ts";
import { parseTimeString } from "~/shared/utils/time-utils.ts";

import { parseCombinedAddress } from "./parse-combined-address.ts";

type BuildAddressFormInitialValuesOptions = {
	name: StopName;
	stop: ShipmentManagementPickupDeliveryDetails;
	locationTypeOptions: Accessorial[];
	accessorialOptions: Accessorial[];
};

/**
 * Seeds the add/edit address form with the current stop so the user edits the
 * existing values. The combined address string is split back into parts and the
 * location type + accessorial names are mapped back to their option objects
 * (matched by name) for the pickup/delivery card.
 */
export const buildAddressFormInitialValues = ({
	name,
	stop,
	locationTypeOptions,
	accessorialOptions,
}: BuildAddressFormInitialValuesOptions): Record<string, unknown> => {
	const locationTypeOption = locationTypeOptions.find(
		(option) => option.name === stop.locationType,
	);
	const selectedAccessorials = accessorialOptions.filter((option) =>
		stop.accessorials?.includes(option.name),
	);
	// The get-shipment endpoint only returns the stop address as a single
	// formatted string, so parse it back into parts when the structured fields
	// haven't been populated by a previous address selection/edit.
	const parsed = parseCombinedAddress(stop.address);
	const postalCode = stop.postalCode || parsed.postalCode;

	return {
		companyName: stop.companyName ?? "",
		address1: stop.address1 || parsed.address1,
		address2: stop.address2 ?? "",
		addressPostalCode: postalCode ? { postalCode } : null,
		city: stop.city || parsed.city,
		state: stop.stateProvince || parsed.state,
		country: stop.country || parsed.country,
		[`${name}_contactName`]: stop.contact?.name ?? "",
		[`${name}_contactPhone`]: stop.contact?.phone ?? "",
		[`${name}_contactExtension`]: stop.contact?.extension ?? "",
		[`${name}_hoursOpen`]: parseTimeString(stop.shipmentHoursFrom),
		[`${name}_hoursClose`]: parseTimeString(stop.shipmentHoursTo),
		[`${name}_specialInstructions`]: stop.specialInstructions ?? "",
		[`${name}_accessorials`]: [
			...(locationTypeOption ? [locationTypeOption] : []),
			...selectedAccessorials,
		],
	};
};
